import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api/client';

/**
 * Server-state cache. This is the ONLY place server data lives.
 *
 * Retry policy is deliberate: retrying a 401/403/402/404/422 is pointless —
 * the answer will not change, and retrying a 402 would spam the user's plan
 * limit. Only transient failures (5xx, network) are worth a second attempt.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError) {
    if (error.status >= 400 && error.status < 500) return false;
  }
  return failureCount < 2;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetry,
      // Data is fresh for 30s — stops a tab-switch refetching every list.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      throwOnError: false,
    },
    mutations: {
      // Never auto-retry a write: a retried POST can double-create.
      retry: false,
    },
  },
});

/**
 * Query key factory. Centralised so invalidation is reliable —
 * `queryClient.invalidateQueries({ queryKey: qk.projects.all })` invalidates
 * every project list and detail in one call, and a typo can't silently miss.
 */
export const qk = {
  auth: { me: ['auth', 'me'] as const },
  roles: {
    all: ['roles'] as const,
    list: (params?: unknown) => ['roles', 'list', params] as const,
    byId: (id: string) => ['roles', id] as const,
    permissions: ['roles', 'permissions'] as const,
  },
  settings: {
    all: ['settings'] as const,
    values: ['settings', 'values'] as const,
    catalogue: ['settings', 'catalogue'] as const,
    publicBranding: (slug: string) => ['settings', 'public', slug] as const,
  },
  calendarSync: {
    all: ['calendar-sync'] as const,
    providers: ['calendar-sync', 'providers'] as const,
    integrations: ['calendar-sync', 'integrations'] as const,
    oauthApps: ['calendar-sync', 'oauth-apps'] as const,
  },
  leadSource: {
    all: ['lead-source'] as const,
    providers: ['lead-source', 'providers'] as const,
    integrations: ['lead-source', 'integrations'] as const,
    runs: (id: string) => ['lead-source', 'runs', id] as const,
    metaConnection: ['lead-source', 'meta-connection'] as const,
    metaPages: ['lead-source', 'meta-pages'] as const,
    metaForms: (pageId: string) => ['lead-source', 'meta-forms', pageId] as const,
  },
  webhooks: {
    all: ['webhooks'] as const,
    list: (params?: unknown) => ['webhooks', 'list', params] as const,
  },
  monitoring: {
    all: ['monitoring'] as const,
    settings: ['monitoring', 'settings'] as const,
    devices: (params?: unknown) => ['monitoring', 'devices', params] as const,
    sessions: (params?: unknown) => ['monitoring', 'sessions', params] as const,
    screenshots: (params?: unknown) => ['monitoring', 'screenshots', params] as const,
    events: (params?: unknown) => ['monitoring', 'events', params] as const,
  },
  mailboxes: {
    all: ['mailboxes'] as const,
    list: ['mailboxes', 'list'] as const,
    threads: (params?: unknown) => ['mailboxes', 'threads', params] as const,
    thread: (threadId: string) => ['mailboxes', 'threads', threadId] as const,
  },
  whatsappNumbers: {
    all: ['whatsapp-numbers'] as const,
    list: ['whatsapp-numbers', 'list'] as const,
    threads: (params?: unknown) => ['whatsapp-numbers', 'threads', params] as const,
    thread: (threadId: string) => ['whatsapp-numbers', 'threads', threadId] as const,
    botFlow: (numberId: string) => ['whatsapp-numbers', 'bot-flow', numberId] as const,
  },
  whatsappCampaigns: {
    all: ['whatsapp-campaigns'] as const,
    list: (params?: unknown) => ['whatsapp-campaigns', 'list', params] as const,
    detail: (id: string) => ['whatsapp-campaigns', 'detail', id] as const,
    recipients: (id: string) => ['whatsapp-campaigns', 'recipients', id] as const,
  },
  billing: {
    all: ['billing'] as const,
    subscription: ['billing', 'subscription'] as const,
    usage: ['billing', 'usage'] as const,
    plans: ['billing', 'plans'] as const,
    invoices: (params?: unknown) => ['billing', 'invoices', params] as const,
  },
  compliance: {
    all: ['compliance'] as const,
    consents: ['compliance', 'consents'] as const,
    deletionRequests: (status?: string) => ['compliance', 'deletion-requests', status] as const,
  },
  projects: {
    all: ['projects'] as const,
    list: (params?: unknown) => ['projects', 'list', params] as const,
    detail: (id: string) => ['projects', 'detail', id] as const,
    stats: ['projects', 'stats'] as const,
    members: (id: string) => ['projects', 'members', id] as const,
    categories: (params?: unknown) => ['projects', 'categories', params] as const,
    categoryOptions: ['projects', 'categories', 'options'] as const,
    templates: (params?: unknown) => ['projects', 'templates', params] as const,
    templateOptions: ['projects', 'templates', 'options'] as const,
    template: (id: string) => ['projects', 'templates', 'detail', id] as const,
  },
  tasks: {
    all: ['tasks'] as const,
    list: (params?: unknown) => ['tasks', 'list', params] as const,
    stats: ['tasks', 'stats'] as const,
    detail: (id: string) => ['tasks', 'detail', id] as const,
    board: (projectId: string) => ['tasks', 'board', projectId] as const,
    dependencies: (id: string) => ['tasks', 'dependencies', id] as const,
    dependencyEdges: (projectId: string) => ['tasks', 'dependencyEdges', projectId] as const,
    timeEntries: (id: string) => ['tasks', 'time-entries', id] as const,
  },
  milestones: {
    all: ['milestones'] as const,
    list: (params?: unknown) => ['milestones', 'list', params] as const,
    detail: (id: string) => ['milestones', 'detail', id] as const,
  },
  deliverables: {
    all: ['deliverables'] as const,
    list: (params?: unknown) => ['deliverables', 'list', params] as const,
    detail: (id: string) => ['deliverables', 'detail', id] as const,
  },
  sprints: {
    all: ['sprints'] as const,
    list: (params?: unknown) => ['sprints', 'list', params] as const,
    detail: (id: string) => ['sprints', 'detail', id] as const,
    burndown: (id: string) => ['sprints', 'burndown', id] as const,
    velocity: (projectId: string) => ['sprints', 'velocity', projectId] as const,
  },
  leads: {
    all: ['leads'] as const,
    list: (params?: unknown) => ['leads', 'list', params] as const,
    detail: (id: string) => ['leads', 'detail', id] as const,
    stats: (range?: unknown) => ['leads', 'stats', range] as const,
    assignees: (id: string) => ['leads', 'assignees', id] as const,
  },
  leadOptions: {
    all: ['leadOptions'] as const,
    statuses: ['leadOptions', 'statuses'] as const,
    sources: ['leadOptions', 'sources'] as const,
    countries: ['leadOptions', 'countries'] as const,
    states: (countryId?: string) => ['leadOptions', 'states', countryId ?? null] as const,
    cities: (stateId?: string) => ['leadOptions', 'cities', stateId ?? null] as const,
  },
  deals: {
    all: ['deals'] as const,
    list: (params?: unknown) => ['deals', 'list', params] as const,
    detail: (id: string) => ['deals', 'detail', id] as const,
    board: (pipelineId?: string) => ['deals', 'board', pipelineId ?? null] as const,
    forecast: (pipelineId?: string) => ['deals', 'forecast', pipelineId ?? null] as const,
  },
  companies: {
    all: ['companies'] as const,
    list: (params?: unknown) => ['companies', 'list', params] as const,
    detail: (id: string) => ['companies', 'detail', id] as const,
    stats: ['companies', 'stats'] as const,
  },
  contacts: {
    all: ['contacts'] as const,
    list: (params?: unknown) => ['contacts', 'list', params] as const,
    detail: (id: string) => ['contacts', 'detail', id] as const,
  },
  pipelines: {
    all: ['pipelines'] as const,
  },
  territories: {
    all: ['territories'] as const,
    list: (params?: unknown) => ['territories', 'list', params] as const,
    detail: (id: string) => ['territories', 'detail', id] as const,
    options: ['territories', 'options'] as const,
    members: (id: string) => ['territories', 'members', id] as const,
  },
  services: {
    all: ['services'] as const,
    list: (params?: unknown) => ['services', 'list', params] as const,
    detail: (id: string) => ['services', 'detail', id] as const,
    options: ['services', 'options'] as const,
  },
  sequences: {
    all: ['sequences'] as const,
    list: (params?: unknown) => ['sequences', 'list', params] as const,
    detail: (id: string) => ['sequences', 'detail', id] as const,
    options: ['sequences', 'options'] as const,
    enrollments: (id: string) => ['sequences', 'enrollments', id] as const,
  },
  quotes: {
    all: ['quotes'] as const,
    list: (params?: unknown) => ['quotes', 'list', params] as const,
    stats: (params?: unknown) => ['quotes', 'stats', params] as const,
    detail: (id: string) => ['quotes', 'detail', id] as const,
    templates: ['quotes', 'templates'] as const,
    templateBuiltins: ['quotes', 'template-builtins'] as const,
  },
  invoices: {
    all: ['invoices'] as const,
    list: (params?: unknown) => ['invoices', 'list', params] as const,
    detail: (id: string) => ['invoices', 'detail', id] as const,
    stats: ['invoices', 'stats'] as const,
    templates: ['invoices', 'templates'] as const,
    templateBuiltins: ['invoices', 'template-builtins'] as const,
    recurring: {
      all: ['invoices', 'recurring'] as const,
      list: (params?: unknown) => ['invoices', 'recurring', 'list', params] as const,
      detail: (id: string) => ['invoices', 'recurring', 'detail', id] as const,
    },
  },
  expenses: {
    all: ['expenses'] as const,
    list: (params?: unknown) => ['expenses', 'list', params] as const,
  },
  expenseCategories: {
    all: ['expenseCategories'] as const,
    list: (params?: unknown) => ['expenseCategories', 'list', params] as const,
    options: ['expenseCategories', 'options'] as const,
  },
  banks: {
    all: ['banks'] as const,
    list: (params?: unknown) => ['banks', 'list', params] as const,
    options: ['banks', 'options'] as const,
    statement: (bankAccountId: string, params?: unknown) => ['banks', 'statement', bankAccountId, params] as const,
    matchCandidates: (bankAccountId: string, lineId: string) => ['banks', 'statement', bankAccountId, 'candidates', lineId] as const,
  },
  payments: {
    all: ['payments'] as const,
    list: (params?: unknown) => ['payments', 'list', params] as const,
  },
  ledger: {
    all: ['ledger'] as const,
    view: (params?: unknown) => ['ledger', 'view', params] as const,
  },
  advances: {
    all: ['advances'] as const,
    list: (params?: unknown) => ['advances', 'list', params] as const,
    detail: (id: string) => ['advances', 'detail', id] as const,
  },
  analytics: {
    businessHealthCurrent: ['analytics', 'business-health'] as const,
    businessHealthTrend: (days: number) => ['analytics', 'business-health', 'trend', days] as const,
  },
  reports: {
    all: ['reports'] as const,
    summary: (period: string) => ['reports', 'summary', period] as const,
    revenueTrend: (period: string) => ['reports', 'revenue-trend', period] as const,
    funnel: ['reports', 'funnel'] as const,
    leaderboard: (period: string) => ['reports', 'leaderboard', period] as const,
    financeSummary: ['reports', 'finance-summary'] as const,
    financeTrend: (period: string) => ['reports', 'finance-trend', period] as const,
    topDebtors: ['reports', 'top-debtors'] as const,
    sources: ['reports', 'sources'] as const,
    definitions: (params?: unknown) => ['reports', 'definitions', params] as const,
    definition: (id: string) => ['reports', 'definitions', id] as const,
    pmProjectKpis: (filters?: unknown) => ['reports', 'pm', 'kpis', 'projects', filters] as const,
    pmTaskKpis: (filters?: unknown) => ['reports', 'pm', 'kpis', 'tasks', filters] as const,
    pmProjectsTable: (params?: unknown) => ['reports', 'pm', 'table', 'projects', params] as const,
    pmTasksTable: (params?: unknown) => ['reports', 'pm', 'table', 'tasks', params] as const,
    pmEmployeePerformance: (filters?: unknown) => ['reports', 'pm', 'employee-performance', filters] as const,
    pmCharts: (filters?: unknown) => ['reports', 'pm', 'charts', filters] as const,
    pmWorkload: (filters?: unknown) => ['reports', 'pm', 'workload', filters] as const,
    pmDelayAnalysis: (filters?: unknown) => ['reports', 'pm', 'delay-analysis', filters] as const,
    pmBudget: (filters?: unknown) => ['reports', 'pm', 'budget', filters] as const,
    pmTaskAging: (filters?: unknown) => ['reports', 'pm', 'task-aging', filters] as const,
    pmTimeTracking: (filters?: unknown) => ['reports', 'pm', 'time-tracking', filters] as const,
    salesLeadKpis: (filters?: unknown) => ['reports', 'sales', 'kpis', 'leads', filters] as const,
    salesDealKpis: (filters?: unknown) => ['reports', 'sales', 'kpis', 'deals', filters] as const,
    salesFollowUpKpis: (filters?: unknown) => ['reports', 'sales', 'kpis', 'follow-ups', filters] as const,
    salesLeadsTable: (params?: unknown) => ['reports', 'sales', 'table', 'leads', params] as const,
    salesDealsTable: (params?: unknown) => ['reports', 'sales', 'table', 'deals', params] as const,
    salesFunnelConversion: (filters?: unknown) => ['reports', 'sales', 'funnel-conversion', filters] as const,
    salesRevenue: (filters?: unknown) => ['reports', 'sales', 'revenue', filters] as const,
    salesPipeline: (filters?: unknown) => ['reports', 'sales', 'pipeline', filters] as const,
    salesEmployeePerformance: (filters?: unknown) => ['reports', 'sales', 'employee-performance', filters] as const,
    salesLeadSources: (filters?: unknown) => ['reports', 'sales', 'lead-sources', filters] as const,
    salesFollowUps: (filters?: unknown) => ['reports', 'sales', 'follow-ups', filters] as const,
    salesLostDeals: (filters?: unknown) => ['reports', 'sales', 'lost-deals', filters] as const,
    salesForecast: (filters?: unknown) => ['reports', 'sales', 'forecast', filters] as const,
    salesSummaryDeltas: (period: string, filters?: unknown) => ['reports', 'sales', 'summary-deltas', period, filters] as const,
    salesFollowUpsList: (bucket: string, filters?: unknown) => ['reports', 'sales', 'follow-ups', 'list', bucket, filters] as const,
    salesQuotes: (filters?: unknown) => ['reports', 'sales', 'quotes', filters] as const,
    salesActivity: (filters?: unknown) => ['reports', 'sales', 'activity', filters] as const,
    salesDealAging: (filters?: unknown) => ['reports', 'sales', 'deal-aging', filters] as const,
    salesTerritories: (filters?: unknown) => ['reports', 'sales', 'territories', filters] as const,
  },
  salesTargets: {
    all: ['sales-targets'] as const,
    list: (params?: unknown) => ['sales-targets', 'list', params] as const,
    detail: (id: string) => ['sales-targets', 'detail', id] as const,
  },
  agency: {
    retainers: {
      all: ['agency', 'retainers'] as const,
      list: (params?: unknown) => ['agency', 'retainers', 'list', params] as const,
      detail: (id: string) => ['agency', 'retainers', 'detail', id] as const,
    },
    campaigns: {
      all: ['agency', 'campaigns'] as const,
      list: (params?: unknown) => ['agency', 'campaigns', 'list', params] as const,
      detail: (id: string) => ['agency', 'campaigns', 'detail', id] as const,
    },
    seoReports: {
      // A true prefix of list()'s key (one element shorter) -- required for
      // invalidateQueries to prefix-match every params variant of list();
      // list(campaignId) alone (params defaulting to undefined) is the SAME
      // length as a real list() call and does NOT prefix-match it, which
      // silently no-ops the invalidation (confirmed live: a created SEO
      // report never appeared until a manual refetch, before this existed).
      all: (campaignId: string) => ['agency', 'seo-reports', campaignId] as const,
      list: (campaignId: string, params?: unknown) => ['agency', 'seo-reports', campaignId, params] as const,
    },
  },
  marketplaceApps: {
    all: ['marketplace-apps'] as const,
    list: (params?: unknown) => ['marketplace-apps', 'list', params] as const,
    detail: (id: string) => ['marketplace-apps', 'detail', id] as const,
  },
  pluginInstallations: {
    all: ['plugin-installations'] as const,
    list: (params?: unknown) => ['plugin-installations', 'list', params] as const,
    browse: (params?: unknown) => ['plugin-installations', 'browse', params] as const,
  },
  aiEmployees: {
    latest: (role: string) => ['ai-employees', role, 'latest'] as const,
    history: (role: string) => ['ai-employees', role, 'history'] as const,
  },
  dealSuggestions: {
    all: () => ['deal-suggestions'] as const,
    list: () => ['deal-suggestions', 'list'] as const,
  },
  aiAssistant: {
    conversations: ['ai-assistant', 'conversations'] as const,
    conversation: (id: string) => ['ai-assistant', 'conversation', id] as const,
  },
  assets: {
    all: ['assets'] as const,
    list: (params?: unknown) => ['assets', 'list', params] as const,
    detail: (id: string) => ['assets', 'detail', id] as const,
    stats: ['assets', 'stats'] as const,
    mine: ['assets', 'mine'] as const,
    assignments: (id: string) => ['assets', 'assignments', id] as const,
    maintenance: (id: string) => ['assets', 'maintenance', id] as const,
    depreciation: (id: string, asOf?: string) => ['assets', 'depreciation', id, asOf ?? null] as const,
    categories: (params?: unknown) => ['assets', 'categories', params] as const,
    categoryOptions: ['assets', 'categories', 'options'] as const,
  },
  inventory: {
    all: ['inventory'] as const,
    list: (params?: unknown) => ['inventory', 'list', params] as const,
    detail: (id: string) => ['inventory', 'detail', id] as const,
    stats: ['inventory', 'stats'] as const,
    movements: (id: string, params?: unknown) => ['inventory', 'movements', id, params] as const,
    categories: (params?: unknown) => ['inventory', 'categories', params] as const,
    categoryOptions: ['inventory', 'categories', 'options'] as const,
    warehouses: (params?: unknown) => ['inventory', 'warehouses', params] as const,
    warehouseOptions: ['inventory', 'warehouses', 'options'] as const,
  },
  audit: {
    all: ['audit'] as const,
    list: (params?: unknown) => ['audit', 'list', params] as const,
    facets: ['audit', 'facets'] as const,
  },
  procurement: {
    vendorCategories: {
      all: ['procurement', 'vendorCategories'] as const,
      list: (params?: unknown) => ['procurement', 'vendorCategories', 'list', params] as const,
      options: ['procurement', 'vendorCategories', 'options'] as const,
    },
    vendors: {
      all: ['procurement', 'vendors'] as const,
      list: (params?: unknown) => ['procurement', 'vendors', 'list', params] as const,
      detail: (id: string) => ['procurement', 'vendors', 'detail', id] as const,
      options: ['procurement', 'vendors', 'options'] as const,
    },
    purchaseRequests: {
      all: ['procurement', 'purchaseRequests'] as const,
      list: (params?: unknown) => ['procurement', 'purchaseRequests', 'list', params] as const,
      detail: (id: string) => ['procurement', 'purchaseRequests', 'detail', id] as const,
    },
    rfqs: {
      all: ['procurement', 'rfqs'] as const,
      list: (params?: unknown) => ['procurement', 'rfqs', 'list', params] as const,
      detail: (id: string) => ['procurement', 'rfqs', 'detail', id] as const,
    },
    purchaseOrders: {
      all: ['procurement', 'purchaseOrders'] as const,
      list: (params?: unknown) => ['procurement', 'purchaseOrders', 'list', params] as const,
      detail: (id: string) => ['procurement', 'purchaseOrders', 'detail', id] as const,
    },
    vendorInvoices: {
      all: ['procurement', 'vendorInvoices'] as const,
      list: (params?: unknown) => ['procurement', 'vendorInvoices', 'list', params] as const,
      detail: (id: string) => ['procurement', 'vendorInvoices', 'detail', id] as const,
    },
  },
  gl: {
    accounts: {
      all: ['gl', 'accounts'] as const,
      list: (params?: unknown) => ['gl', 'accounts', 'list', params] as const,
      detail: (id: string) => ['gl', 'accounts', 'detail', id] as const,
      options: ['gl', 'accounts', 'options'] as const,
    },
    entries: {
      all: ['gl', 'entries'] as const,
      list: (params?: unknown) => ['gl', 'entries', 'list', params] as const,
      detail: (id: string) => ['gl', 'entries', 'detail', id] as const,
    },
    reports: {
      trialBalance: (asOf?: string) => ['gl', 'reports', 'trial-balance', asOf] as const,
      pnl: (from?: string, to?: string) => ['gl', 'reports', 'pnl', from, to] as const,
      balanceSheet: (asOf?: string) => ['gl', 'reports', 'balance-sheet', asOf] as const,
      cashFlow: (from?: string, to?: string) => ['gl', 'reports', 'cash-flow', from, to] as const,
    },
  },
  taxCompliance: {
    filings: {
      all: ['taxCompliance', 'filings'] as const,
      list: (params?: unknown) => ['taxCompliance', 'filings', 'list', params] as const,
      detail: (id: string) => ['taxCompliance', 'filings', 'detail', id] as const,
    },
    summary: ['taxCompliance', 'summary'] as const,
  },
  budget: {
    lines: {
      all: ['budget', 'lines'] as const,
      list: (params?: unknown) => ['budget', 'lines', 'list', params] as const,
      detail: (id: string) => ['budget', 'lines', 'detail', id] as const,
    },
    summary: ['budget', 'summary'] as const,
  },
  users: {
    all: ['users'] as const,
    list: (params?: unknown) => ['users', 'list', params] as const,
  },
  portalTickets: {
    all: ['portalTickets'] as const,
    list: (params?: unknown) => ['portalTickets', 'list', params] as const,
    detail: (id: string) => ['portalTickets', 'detail', id] as const,
  },
  portal: {
    me: ['portal', 'me'] as const,
    overview: ['portal', 'overview'] as const,
    documents: {
      all: ['portal', 'documents'] as const,
      list: (params?: unknown) => ['portal', 'documents', 'list', params] as const,
    },
    tickets: {
      all: ['portal', 'tickets'] as const,
      list: (params?: unknown) => ['portal', 'tickets', 'list', params] as const,
      detail: (id: string) => ['portal', 'tickets', 'detail', id] as const,
    },
    notifications: {
      all: ['portal', 'notifications'] as const,
      list: (params?: unknown) => ['portal', 'notifications', 'list', params] as const,
      unreadCount: ['portal', 'notifications', 'unread-count'] as const,
    },
    projects: {
      all: ['portal', 'projects'] as const,
      list: (params?: unknown) => ['portal', 'projects', 'list', params] as const,
      detail: (id: string) => ['portal', 'projects', 'detail', id] as const,
    },
    invoices: {
      all: ['portal', 'invoices'] as const,
      list: (params?: unknown) => ['portal', 'invoices', 'list', params] as const,
      detail: (id: string) => ['portal', 'invoices', 'detail', id] as const,
    },
    deliverables: {
      all: ['portal', 'deliverables'] as const,
      list: (params?: unknown) => ['portal', 'deliverables', 'list', params] as const,
      detail: (id: string) => ['portal', 'deliverables', 'detail', id] as const,
    },
    purchaseOrders: {
      all: ['portal', 'purchaseOrders'] as const,
      list: (params?: unknown) => ['portal', 'purchaseOrders', 'list', params] as const,
      detail: (id: string) => ['portal', 'purchaseOrders', 'detail', id] as const,
    },
    vendorInvoices: {
      all: ['portal', 'vendorInvoices'] as const,
      list: (params?: unknown) => ['portal', 'vendorInvoices', 'list', params] as const,
      detail: (id: string) => ['portal', 'vendorInvoices', 'detail', id] as const,
    },
  },
  activities: {
    all: ['activities'] as const,
    timeline: (relatedType: string, relatedId: string) =>
      ['activities', 'timeline', relatedType, relatedId] as const,
    list: (params?: unknown) => ['activities', 'list', params] as const,
  },
  discussions: {
    all: ['discussions'] as const,
    thread: (relatedType: string, relatedId: string) => ['discussions', 'thread', relatedType, relatedId] as const,
  },
  chat: {
    all: ['chat'] as const,
    conversation: (relatedType: string, relatedId: string) => ['chat', 'conversation', relatedType, relatedId] as const,
    messages: (conversationId: string) => ['chat', 'messages', conversationId] as const,
    unreadCount: ['chat', 'unread-count'] as const,
  },
  videoCalls: {
    all: ['video-calls'] as const,
    active: (relatedType: string, relatedId: string) => ['video-calls', 'active', relatedType, relatedId] as const,
  },
  files: {
    all: ['files'] as const,
    list: (relatedType: string, relatedId: string) => ['files', 'list', relatedType, relatedId] as const,
  },
  documents: {
    all: ['documents'] as const,
    list: (params?: unknown) => ['documents', 'list', params] as const,
    versions: (id: string) => ['documents', 'versions', id] as const,
    shares: (id: string) => ['documents', 'shares', id] as const,
    portalShares: (id: string) => ['documents', 'portal-shares', id] as const,
    trash: (params?: unknown) => ['documents', 'trash', params] as const,
  },
  folders: {
    all: ['folders'] as const,
    list: ['folders', 'list'] as const,
    shares: (id: string) => ['folders', 'shares', id] as const,
  },
  automation: {
    all: ['automation'] as const,
    list: (params?: unknown) => ['automation', 'list', params] as const,
    detail: (id: string) => ['automation', 'detail', id] as const,
    stats: ['automation', 'stats'] as const,
    runs: (params?: unknown) => ['automation', 'runs', params] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    list: (params?: unknown) => ['notifications', 'list', params] as const,
    unreadCount: ['notifications', 'unread-count'] as const,
    prefs: ['notifications', 'prefs'] as const,
    pushConfig: ['notifications', 'push-config'] as const,
  },
  calendar: {
    all: ['calendar'] as const,
    range: (from: string, to: string, ownerId?: string) => ['calendar', 'range', from, to, ownerId ?? null] as const,
    upcoming: (limit: number, ownerId?: string) => ['calendar', 'upcoming', limit, ownerId ?? null] as const,
  },
  hrms: {
    all: ['hrms'] as const,
    overview: ['hrms', 'overview'] as const,
    employees: (params?: unknown) => ['hrms', 'employees', params] as const,
    employeeStats: ['hrms', 'employees', 'stats'] as const,
    employee: (id: string) => ['hrms', 'employee', id] as const,
    departments: (params?: unknown) => ['hrms', 'departments', params] as const,
    departmentOptions: ['hrms', 'departments', 'options'] as const,
    designations: (params?: unknown) => ['hrms', 'designations', params] as const,
    designationOptions: ['hrms', 'designations', 'options'] as const,
    leave: (params?: unknown) => ['hrms', 'leave', params] as const,
    leaveStats: ['hrms', 'leave', 'stats'] as const,
    myLeave: (params?: unknown) => ['hrms', 'leave', 'mine', params] as const,
    wfh: (params?: unknown) => ['hrms', 'wfh', params] as const,
    myWfh: (params?: unknown) => ['hrms', 'wfh', 'mine', params] as const,
    attendance: (params?: unknown) => ['hrms', 'attendance', params] as const,
    attendanceToday: ['hrms', 'attendance', 'today'] as const,
    myAttendance: (params?: unknown) => ['hrms', 'attendance', 'mine', params] as const,
    kioskToken: (target?: { locationId?: string; branchId?: string }) => ['hrms', 'attendance', 'kiosk-token', target?.locationId ?? null, target?.branchId ?? null] as const,
    kioskLocations: ['hrms', 'attendance', 'kiosk-locations'] as const,
    attendanceMeToday: ['hrms', 'attendance', 'me-today'] as const,
    faceStatus: ['hrms', 'attendance', 'face-status'] as const,
    attendanceSettings: ['hrms', 'attendance-admin', 'settings'] as const,
    attendanceLocations: (params?: unknown) => ['hrms', 'attendance-admin', 'locations', params] as const,
    locationAssignees: (id: string) => ['hrms', 'attendance-admin', 'locations', id, 'users'] as const,
    attendanceReportRows: (params?: unknown) => ['hrms', 'attendance-admin', 'report-rows', params] as const,
    attendanceReportSummary: (params?: unknown) => ['hrms', 'attendance-admin', 'report-summary', params] as const,
    attendanceEvents: (params?: unknown) => ['hrms', 'attendance-admin', 'events', params] as const,
    verifyQr: (token: string) => ['hrms', 'attendance', 'verify-qr', token] as const,
    payrollRuns: (params?: unknown) => ['hrms', 'payroll', 'runs', params] as const,
    payrollRun: (id: string) => ['hrms', 'payroll', 'run', id] as const,
    employeePayslips: (id: string) => ['hrms', 'payroll', 'payslips', id] as const,
    myPayslips: ['hrms', 'payroll', 'mine'] as const,
    salaryStructures: (employeeId: string) => ['hrms', 'salary-structures', employeeId] as const,
    bonuses: (params?: unknown) => ['hrms', 'bonuses', params] as const,
    loans: (params?: unknown) => ['hrms', 'loans', params] as const,
    branches: (params?: unknown) => ['hrms', 'branches', params] as const,
    branchOptions: ['hrms', 'branches', 'options'] as const,
    holidays: (params?: unknown) => ['hrms', 'holidays', params] as const,
    workweek: ['hrms', 'holidays', 'workweek'] as const,
    teams: (params?: unknown) => ['hrms', 'teams', params] as const,
    teamOptions: ['hrms', 'teams', 'options'] as const,
    shifts: (params?: unknown) => ['hrms', 'shifts', params] as const,
    shiftOptions: ['hrms', 'shifts', 'options'] as const,
    documents: (params?: unknown) => ['hrms', 'documents', params] as const,
    reviewCycles: (params?: unknown) => ['hrms', 'performance', 'cycles', params] as const,
    cycleOptions: ['hrms', 'performance', 'cycles', 'options'] as const,
    reviews: (params?: unknown) => ['hrms', 'performance', 'reviews', params] as const,
    performanceStats: (cycleId?: string) => ['hrms', 'performance', 'stats', cycleId ?? null] as const,
    jobs: (params?: unknown) => ['hrms', 'recruitment', 'jobs', params] as const,
    jobOptions: ['hrms', 'recruitment', 'jobs', 'options'] as const,
    candidates: (params?: unknown) => ['hrms', 'recruitment', 'candidates', params] as const,
    recruitmentStats: ['hrms', 'recruitment', 'stats'] as const,
    exits: (params?: unknown) => ['hrms', 'exit', params] as const,
    exitStats: ['hrms', 'exit', 'stats'] as const,
    leaveBalances: (employeeId: string, year?: number) => ['hrms', 'leave', 'balances', employeeId, year ?? null] as const,
    attendanceSummary: (params?: unknown) => ['hrms', 'attendance', 'summary', params] as const,
  },
} as const;
